var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import { ConfigProvider as AntThemeProvider, RobinTheme, } from '@robinpowered/ui-kit';
import { IntegrationsProvider } from './context/IntegrationContext';
import { ComposerMode } from './types/index';
import { FeatureFlagProvider } from '@robinpowered/dashboard-apps-common';
import { ConfigProvider } from './context/ConfigContext';
import { useRequiredContexts } from './hooks/useRequiredContexts';
import { LocalizationContext } from './context/LocalizationContext';
import { CreateEventComposerInternal } from './composers/CreateEventComposer';
import { EditEventComposerInternal } from './composers/EditEventComposer';
/**
 * Internal implementation of EventComposer.
 * Assumes all required contexts (ApolloProvider, etc.) are already present.
 */
var EventComposerInternal = function (_a) {
    var props = __rest(_a, []);
    var isCreateMode = props.composerMode === ComposerMode.CREATE;
    var isEditMode = props.composerMode === ComposerMode.EDIT;
    var _b = props.config, tenantId = _b.tenantId, accessToken = _b.accessToken;
    var featureFlagUrl = props.config.environmentUrls.featureFlagUrl;
    // Memoize all error callbacks to prevent child providers' useEffects from re-running
    var handleEventComposerContextError = useCallback(function (error) {
        var _a;
        (_a = props.onError) === null || _a === void 0 ? void 0 : _a.call(props, error, props.composerMode);
    }, 
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [props.onError, props.composerMode]);
    return (_jsx(ConfigProvider, { config: props.config, children: _jsx(FeatureFlagProvider, { featureFlagUrl: featureFlagUrl, tenantId: tenantId || null, accessToken: accessToken, onError: function (message) {
                return handleEventComposerContextError(new Error(message));
            }, children: _jsxs(IntegrationsProvider, { children: [isCreateMode ? _jsx(CreateEventComposerInternal, __assign({}, props)) : null, isEditMode ? _jsx(EditEventComposerInternal, __assign({}, props)) : null] }) }) }));
};
/**
 * EventComposer component with context validation.
 *
 * This component validates that all required React contexts (e.g., ApolloProvider)
 * are present before rendering. If contexts are missing, it throws a helpful error
 * message with setup instructions.
 *
 * @example
 * ```tsx
 * import { ApolloProvider } from '@apollo/client';
 * import { EventComposer } from '@robinpowered/dashboard-apps-patterns';
 *
 * <ApolloProvider client={apolloClient}>
 *   <EventComposer
 *     config={config}
 *     composerMode="CREATE"
 *     event={eventData}
 *     onSubmit={handleSubmit}
 *     onClose={handleClose}
 *     timeFormat="h:mm a"
 *     selectedTimezone="America/New_York"
 *   />
 * </ApolloProvider>
 * ```
 */
export var EventComposer = function (props) {
    var _a;
    // Validate required contexts before rendering
    useRequiredContexts();
    /* @TODOremove this once we're migrated to the new API fully */
    if (!props.config.environmentUrls.baseApiUrl) {
        (_a = props.onError) === null || _a === void 0 ? void 0 : _a.call(props, new Error('Base API URL is not set'), props.composerMode);
        props.onClose();
        return null;
    }
    // If validation passes, render the internal component
    return (_jsx(AntThemeProvider, { theme: __assign(__assign({}, RobinTheme), { cssVar: { key: 'composer' } }), prefixCls: "ant", children: _jsx(LocalizationContext, { children: _jsx(EventComposerInternal, __assign({}, props)) }) }));
};
